import type { HTMLAttributes, ReactNode } from 'react';
import { kaiFontFamily, styleguideColorsExtended as colors } from '../../../core/tokens';
import { resolveStyleOverride, type StyleOverride } from '../../styleOverrides';
import { kaiTypographyWeb } from '../../typography';

export interface GallerySectionHeaderProps extends HTMLAttributes<HTMLDivElement> {
  title: string;
  meta?: string;
  accentColor?: string;
  collapsed?: boolean;
  onTitleSelect?: () => void;
  onToggle?: () => void;
  actions?: ReactNode;
  styleOverride?: StyleOverride;
}

export const GallerySectionHeader = ({
  title,
  meta,
  accentColor,
  collapsed = false,
  onTitleSelect,
  onToggle,
  actions,
  styleOverride: headerCss,
  style,
  ...props
}: GallerySectionHeaderProps) => {
  const titleStyle = {
    ...kaiTypographyWeb.mediumBold,
    color: colors.ink,
    letterSpacing: 0,
    textTransform: 'uppercase' as const,
  };

  return (
    <div
      data-gallery-section-header='true'
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 9,
        minWidth: 0,
        padding: '9px 0',
        background: colors.paper,
        color: colors.ink,
        fontFamily: kaiFontFamily,
        ...style,
        ...resolveStyleOverride(headerCss),
      }}
      {...props}
    >
      {accentColor
        ? (
          <span
            data-gallery-section-accent='true'
            aria-hidden='true'
            style={{ width: 9, height: 9, flex: '0 0 auto', borderRadius: '50%', backgroundColor: accentColor }}
          />
        )
        : null}

      {onTitleSelect
        ? (
          <button
            type='button'
            aria-label={`Open ${title}`}
            onClick={onTitleSelect}
            style={{
              ...titleStyle,
              border: 0,
              padding: '1px 3px',
              margin: '-1px -3px',
              background: 'transparent',
              cursor: 'pointer',
              fontFamily: 'inherit',
            }}
          >
            {title}
          </button>
        )
        : <span style={titleStyle}>{title}</span>}

      {meta ? <span style={{ ...kaiTypographyWeb.xSmall, color: colors.textSecondary }}>{meta}</span> : null}
      <span aria-hidden='true' style={{ flex: 1, minWidth: 12, height: 1, background: colors.stroke }} />
      {actions ? <span style={{ display: 'flex', alignItems: 'center', gap: 8, flex: '0 0 auto' }}>{actions}</span> : null}

      {onToggle
        ? (
          <button
            type='button'
            aria-label={`${collapsed ? 'Expand' : 'Collapse'} ${title}`}
            aria-expanded={!collapsed}
            onClick={onToggle}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 5,
              flex: '0 0 auto',
              border: 0,
              padding: 0,
              background: 'transparent',
              color: colors.textSecondary,
              cursor: 'pointer',
              ...kaiTypographyWeb.xxSmallBold,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
            }}
          >
            <span>{collapsed ? 'See all' : 'Collapse'}</span>
            <svg
              aria-hidden='true'
              width='11'
              height='11'
              viewBox='0 0 12 12'
              fill='none'
              style={{ transform: collapsed ? 'rotate(-90deg)' : 'rotate(90deg)', transition: 'transform 150ms ease' }}
            >
              <path d='M4.5 3L7.5 6l-3 3' stroke='currentColor' strokeWidth='1.5' strokeLinecap='round' strokeLinejoin='round' />
            </svg>
          </button>
        )
        : null}
    </div>
  );
};
